import React from 'react';
import { FileText, RefreshCw, XCircle, CheckCircle, ArrowRight, Ban } from 'lucide-react';
import WidgetInfoPopover from './WidgetInfoPopover';

const WIDGET_AGENTS = ['Transcript Analysis Agent (Jamba)'];
const WIDGET_PROMPT = `Generate a concise case summary for this fraud claim call.

Include:
- Executive summary (2-3 sentences)
- Key facts (bullet points)
- Suspected fraud type and confidence
- Critical evidence mentioned
- Missing information needed to complete claim
- Recommended next steps
- Overall case status (% complete)

OUTPUT FORMAT (JSON):
{
  "executive_summary": "Customer reports...",
  "key_facts": [...],
  "fraud_assessment": "Account Takeover (85% confidence)",
  "evidence_collected": [...],
  "missing_information": [...],
  "next_steps": [...],
  "completeness_percentage": 70
}

Updated every 15 seconds during active call.`;

const CaseSummary = ({ result, isLoading, error, onRefresh }) => {
  const notApplicable = result?.not_applicable === true;
  const summary = result?.data;
  const hasData = !!summary;

  const completeness = summary?.completeness_percentage ?? 0;
  const completenessColor = completeness >= 75 ? 'text-green-600 bg-green-50 border-green-200'
    : completeness >= 50 ? 'text-yellow-600 bg-yellow-50 border-yellow-200'
    : 'text-red-600 bg-red-50 border-red-200';

  return (
    <div className="bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#000080]/5 to-[#000080]/10">
        <div className="flex items-center gap-2">
          <FileText size={15} style={{ color: '#000080' }} />
          <h3 className="font-semibold text-gray-800 text-sm">Case Summary</h3>
        </div>
        <div className="flex items-center gap-2">
          {hasData && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${completenessColor}`}>
              {completeness}% complete
            </span>
          )}
          {isLoading && hasData && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#000080' }} title="Updating..." />}
          <WidgetInfoPopover agents={WIDGET_AGENTS} prompt={WIDGET_PROMPT} />
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-40"
            title="Regenerate summary"
          >
            <RefreshCw size={14} className={isLoading && !hasData ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="p-4">
        {notApplicable && !isLoading && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            <Ban size={22} className="text-gray-300" />
            <p className="text-xs mt-1 text-gray-500">No summary available — not a fraud claim call</p>
          </div>
        )}

        {!hasData && !notApplicable && (
          <div className="flex flex-col items-center justify-center py-4 text-gray-400 gap-1">
            {isLoading
              ? <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#000080', borderTopColor: 'transparent' }} />
              : <FileText size={22} className="opacity-30" />}
            <p className="text-xs mt-1">{isLoading ? 'Generating summary...' : 'Summary will generate during the call...'}</p>
          </div>
        )}

        {error && !hasData && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-xs text-red-600">
            <XCircle size={14} className="shrink-0 mt-0.5" /><span>{error}</span>
          </div>
        )}

        {hasData && (
          <div className="space-y-3">
            {/* Executive Summary */}
            {summary.executive_summary && (
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Executive Summary</p>
                <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-lg p-2.5">
                  {summary.executive_summary}
                </p>
              </div>
            )}

            {/* Fraud Assessment */}
            {summary.fraud_assessment && (
              <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-100 rounded-lg">
                <span className="text-xs font-semibold text-orange-700 shrink-0">Fraud Assessment:</span>
                <span className="text-xs text-orange-800">{summary.fraud_assessment}</span>
              </div>
            )}

            {/* Key Facts */}
            {summary.key_facts?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Key Facts</p>
                <ul className="space-y-1">
                  {summary.key_facts.map((fact, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                      <CheckCircle size={11} className="text-teal-500 shrink-0 mt-0.5" />
                      {fact}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Evidence */}
            {summary.evidence_collected?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Evidence Collected</p>
                <div className="flex flex-wrap gap-1.5">
                  {summary.evidence_collected.map((ev, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full border" style={{ backgroundColor: 'rgba(0,0,128,0.07)', color: '#000080', borderColor: 'rgba(0,0,128,0.15)' }}>
                      {ev}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Missing Info */}
            {summary.missing_information?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Missing Information</p>
                <div className="flex flex-wrap gap-1.5">
                  {summary.missing_information.map((item, i) => (
                    <span key={i} className="text-xs bg-red-50 text-red-600 border border-red-100 px-2 py-0.5 rounded-full">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Next Steps */}
            {summary.next_steps?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">Next Steps</p>
                <ul className="space-y-1">
                  {summary.next_steps.map((step, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                      <ArrowRight size={11} className="shrink-0 mt-0.5" style={{ color: '#000080' }} />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseSummary;
